import React, {Fragment, PropsWithChildren} from 'react';
import {cn, InlineSvg, RootLayout, RsnSkeleton, SkeletonContextProvider} from '~/library/ui-kit';
import mailIcon from '../../../../../../static/icons/mail-send-line.svg';
import {LoadProblemPane} from '../../../../entities/fallbacks';

function InfoPageLayout({
    symbol,
    title,
    children,
}: {
    symbol: keyof typeof symbols;
    title?: null | string;
    children?: {
        center: React.JSX.Element;
        footer: React.JSX.Element;
    };
}) {
    let props: RootLayout.Props = {};
    if (!title) {
        props = {menu: null, navigateBack: false, menuHeader: null, pageHeader: null};
    } else {
        props = {title: title};
    }

    const Symbol = symbols[symbol];
    return (
        <RootLayout {...props} common>
            <div className='flex flex-1 flex-col items-center justify-center gap-6 text-center'>
                <Symbol />
                {children?.center}
            </div>
            {children?.footer}
        </RootLayout>
    );
}
InfoPageLayout.displayName = 'InfoPage.Layout';

function InfoPageSkeleton({title, gotError, reset}: {title?: string | null} & RsnSkeleton.CanHandleError) {
    return (
        <SkeletonContextProvider value={{stopped: gotError}}>
            <InfoPageLayout symbol='skeleton' title={title}>
                {{
                    center: (
                        <InfoPageCenter
                            heading={<RsnSkeleton width={160} />}
                            details={
                                <Fragment>
                                    <RsnSkeleton width={240} />
                                    <RsnSkeleton width={180} />
                                </Fragment>
                            }
                        />
                    ),
                    footer: (
                        <InfoPageFooter>
                            <RsnSkeleton borderRadius={16} width={'100%'} height={48} />
                            {gotError && <LoadProblemPane reset={reset} />}
                        </InfoPageFooter>
                    ),
                }}
            </InfoPageLayout>
        </SkeletonContextProvider>
    );
}
InfoPageSkeleton.displayName = 'InfoPage.Skeleton';

const symbols = {
    email: EmailSymbol,
    time: TimeSymbol,
    success: SuccessSymbol,
    failure: FailureSymbol,
    attention: AttentionSymbol,
    skeleton: SkeletonSymbol,
};

const symbolClass = 'grid size-24 place-items-center rounded-full';

function SkeletonSymbol() {
    return <RsnSkeleton circle height={96} width={96} />;
}

function InfoPageCenter({
    heading,
    details,
    children,
}: {
    heading: string | React.JSX.Element;
    details: string | React.JSX.Element;
    children?: React.JSX.Element;
}) {
    return (
        <div className='grid gap-2'>
            <h2 className='fv-heading-l text-neutral-100'>{heading}</h2>
            <p className='fv-text-l-regular text-neutral-90 whitespace-pre-line'>{details}</p>
            {children}
        </div>
    );
}
InfoPageCenter.displayName = 'InfoPage.Center';

function InfoPageFooter({children}: PropsWithChildren<{}>) {
    return <div className='grid gap-2'>{children}</div>;
}
InfoPageFooter.displayName = 'InfoPage.Footer';

function EmailSymbol() {
    return (
        <figure className={cn(symbolClass, 'bg-neutral-20 text-neutral-90')}>
            <InlineSvg src={mailIcon} height={60} width={60} />
        </figure>
    );
}

function TimeSymbol() {
    return (
        <figure className={cn(symbolClass, 'bg-warning-10 text-warning-30')}>
            <svg viewBox='0 0 96 96' fill='currentColor' xmlns='http://www.w3.org/2000/svg'>
                <path d='M61.501 68.002L45.501 54.002V28.002H51.4127V51.0461L65.6741 63.8288L61.501 68.002Z' />
            </svg>
        </figure>
    );
}

function SuccessSymbol() {
    return (
        <figure className={cn(symbolClass, 'bg-success-10 text-success-30')}>
            <svg xmlns='http://www.w3.org/2000/svg' fill='currentColor' viewBox='0 0 96 96'>
                <path d='m43 55.932 22.98-22.983 3.537 3.535L43 63.002l-15.91-15.91 3.535-3.535L43 55.932Z' />
            </svg>
        </figure>
    );
}

function FailureSymbol() {
    return (
        <figure className={cn(symbolClass, 'bg-danger-10 text-danger-30')}>
            <svg xmlns='http://www.w3.org/2000/svg' width='96' height='96' fill='currentColor'>
                <path d='m50.732 53.93 1.063-23.929h-8.189l1.17 23.93h5.956ZM43.5 61.747c0 2.287 1.914 4.254 4.254 4.254s4.254-1.968 4.254-4.254c0-2.34-1.914-4.307-4.254-4.307S43.5 59.407 43.5 61.747Z' />
            </svg>
        </figure>
    );
}

function AttentionSymbol() {
    return (
        <figure className='size-24 text-warning-30'>
            <svg xmlns='http://www.w3.org/2000/svg' fill='currentColor' viewBox='0 0 96 96'>
                <path
                    className='fill-warning-10'
                    d='M37.309 6.857c4.752-9.143 16.63-9.143 21.382 0L94.328 75.43C99.079 84.57 93.14 96 83.637 96H12.363C2.86 96-3.079 84.571 1.673 75.429z'
                />
                <path d='M50.732 62.93 51.795 39h-8.189l1.17 23.93zM43.5 70.745c0 2.287 1.914 4.254 4.254 4.254s4.254-1.968 4.254-4.254c0-2.34-1.914-4.307-4.254-4.307S43.5 68.406 43.5 70.746' />
            </svg>
        </figure>
    );
}

export const InfoPage = {
    Layout: InfoPageLayout,
    Skeleton: InfoPageSkeleton,
    Center: InfoPageCenter,
    Footer: InfoPageFooter,
};
